import { useCallback, useRef } from 'react'
import { useApolloClient } from '@apollo/react-hooks'
import { useTranslation } from 'react-i18next'
import { toastr } from 'react-redux-toastr'
import * as Query from '../../../api/gql/query'
import { applyMutationResult } from '../lib/apply-mutation-result'
import { describeScheduleError } from '../lib/errors'
import { useGanttStoreApi } from '../store/ganttStore'
import type { ScheduleDetail, ScheduleMutationResult } from '../types'

/**
 * Serialises schedule mutations behind one promise queue per detail page (spec B §9.8): every
 * call to `run` waits for the previous one to settle, applies its `ScheduleMutationResult` to the
 * `SCHEDULE_DETAIL` cache via `applyMutationResult`, refetches when a dependency touched a foreign
 * schedule, and toasts + rethrows on failure so callers can revert their optimistic UI.
 */
export const useScheduleMutations = (scheduleId: string, refetchDetail: () => Promise<unknown>) => {
  const client = useApolloClient()
  const { t } = useTranslation()
  const api = useGanttStoreApi()
  const tail = useRef<Promise<unknown>>(Promise.resolve())

  const apply = useCallback((result: ScheduleMutationResult) => {
    const q = { query: Query.SCHEDULE_DETAIL, variables: { id: scheduleId } }
    let cached: { schedule: ScheduleDetail } | null = null
    try {
      cached = client.readQuery<{ schedule: ScheduleDetail }>(q)
    } catch {
      cached = null
    }
    if (!cached) return
    client.writeQuery({ ...q, data: { schedule: applyMutationResult(cached.schedule, result) } })
    const localIds = new Set(cached.schedule.activities.map((a) => a.id))
    const touchesForeign = result.dependencies.some((d) => !localIds.has(d.predecessorId) || !localIds.has(d.successorId))
    if (touchesForeign) refetchDetail().catch(() => undefined) // review fix (Minor #12): avoid an unhandled rejection on a transient network failure
  }, [client, scheduleId, refetchDetail])

  const run = useCallback(<T,>(fn: () => Promise<{ data: T }>, pick: (data: T) => ScheduleMutationResult | null): Promise<T> => {
    api.getState().incPending()
    const job = tail.current.catch(() => undefined).then(async () => {
      try {
        const { data } = await fn()
        const result = pick(data)
        if (result) apply(result)
        return data
      } catch (e: any) {
        toastr.error(t('Common_error'), describeScheduleError(e.message, t))
        throw e
      } finally {
        api.getState().decPending()
      }
    })
    tail.current = job
    return job
  }, [api, apply, t])

  return { run }
}
